import { useSnackbar } from 'notistack';
import { useForm } from 'react-hook-form';
import { useState, useEffect } from 'react';
import { ApolloQueryResult } from '@apollo/client';
import { yupResolver } from '@hookform/resolvers/yup';

import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import Divider from '@mui/material/Divider';
import LoadingButton from '@mui/lab/LoadingButton';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';

import {
  WebpicStoreIntegrationType,
  useCreateWebPicIntegrationMutation,
  CreateWebPicIntegrationMutationVariables,
} from 'src/graphql/generated';

import FormProvider from 'src/components/hook-form/form-provider';
import { RHFCheckbox, RHFTextField } from 'src/components/hook-form';

import { DapicFormSchema, dapicConfigFormSchemaDefaultValues } from '../schema/dapic-form-schema';

// ----------------------------------------------------------------------

type TAttributes = 'companyId' | 'haveInvoice' | 'havePdv' | 'integrationToken';

type TCurrentData = Pick<WebpicStoreIntegrationType, TAttributes>;

type Props = {
  open: boolean;
  onClose: () => void;
  disableForm?: boolean;
  currentData?: TCurrentData | null;
  refetch: () => Promise<ApolloQueryResult<any>>;
};

// ----------------------------------------------------------------------

export function ConfigDapicModal({
  open,
  onClose,
  currentData,
  disableForm = false,
  refetch,
}: Props) {
  const [enableForm, setEnableForm] = useState<boolean>(!disableForm);
  const [createWebPicIntegration] = useCreateWebPicIntegrationMutation();
  const { enqueueSnackbar } = useSnackbar();

  const methods = useForm({
    resolver: yupResolver(DapicFormSchema),
    defaultValues: dapicConfigFormSchemaDefaultValues,
  });

  const {
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = methods;

  const handleClose = () => {
    onClose();
    reset();
    setEnableForm(!disableForm);
  };

  const onSubmit = handleSubmit(async (data) => {
    const { companyId, haveInvoice, havePdv, integrationToken } = data;

    const variables: CreateWebPicIntegrationMutationVariables = {
      input: {
        companyId,
        haveInvoice,
        havePdv,
        integrationToken,
      },
    };

    try {
      const { data: response } = await createWebPicIntegration({ variables });

      if (!response?.saveWebpicStoreIntegration?.ok) {
        throw new Error('Não foi possível atualizar o dados!');
      }

      enqueueSnackbar('Cadastro efetuado com sucesso!', { variant: 'success' });
      setEnableForm(false);
      onClose();

      await refetch();
    } catch (error) {
      if (error?.message) {
        enqueueSnackbar(error?.message, { variant: 'error' });

        return;
      }
      enqueueSnackbar('Não foi possível atualizar o dados!', { variant: 'error' });
    }
  });

  useEffect(() => {
    const updateFields = () => {
      if (!currentData) return;

      const { companyId, haveInvoice, havePdv, integrationToken } = currentData;

      reset({
        companyId,
        haveInvoice,
        havePdv,
        integrationToken,
      });
    };

    updateFields();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentData]);

  useEffect(() => {
    setEnableForm(!disableForm);
  }, [disableForm]);

  return (
    <FormProvider methods={methods}>
      <Dialog
        open={open}
        PaperProps={{
          sx: {
            minWidth: { xs: '90%', md: 600 },
          },
        }}
      >
        <DialogTitle textAlign="center" fontSize={20}>
          Integração Dapic
        </DialogTitle>

        <DialogContent
          sx={{
            minWidth: { xs: 300, md: 600 },
            minHeight: 180,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            gap: 2,
            marginBottom: 2,
            py: 2.5,
          }}
        >
          <RHFTextField
            name="integrationToken"
            label="Token de Integração"
            disabled={!enableForm}
          />

          <RHFTextField name="companyId" label="ID da Empresa" disabled={!enableForm} />

          <Stack direction="row">
            <RHFCheckbox name="havePdv" label="PDV" disabled={!enableForm} />

            <RHFCheckbox name="haveInvoice" label="Fatura" disabled={!enableForm} />
          </Stack>
        </DialogContent>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <DialogActions>
          <Button variant="outlined" color="inherit" onClick={handleClose}>
            Fechar
          </Button>
          {enableForm && (
            <LoadingButton
              variant="contained"
              color="primary"
              type="submit"
              loading={isSubmitting}
              onClick={onSubmit}
            >
              Salvar
            </LoadingButton>
          )}
          {!enableForm && (
            <Button variant="contained" color="primary" onClick={() => setEnableForm(true)}>
              {currentData ? 'Editar' : 'Criar'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </FormProvider>
  );
}
